import { createContext, type ReactNode, use, useRef, useState } from "react";
import { type BootPreset, BootScreen, type WipePhase } from "./boot-screen";

export type { WipePhase } from "./boot-screen";

/**
 * BootContext - host plumbing for the splash overlay.
 *
 * A single `<BootProvider>` wraps the app root; anywhere in the tree, hooks
 * call `useBoot()` to:
 *
 *   - `show({ mode, status })` - re-display the boot (e.g. after onboarding,
 *     while we wait for the plan API). Resets progress to 0.
 *   - `setProgress(0..1)` - drive the bar from real fetch state. The
 *     internal step driver shuts down on the first call.
 *   - `setStatus(label)` - override the auto-derived status word ("Composing
 *     your plan", "Generating microsteps", etc.).
 *   - `finish()` - ramp to 100% + wipe + unmount the overlay.
 *
 * The provider always renders the boot mounted on first paint; `visible`
 * starts true so the splash covers app boot. A consumer flips it off via
 * `finish()` once initial data is in.
 */

interface BootApi {
  /** True while the boot overlay is mounted. */
  visible: boolean;
  /** Current wipe-state phase, for consumers that need to coordinate
   *  audio / haptics with the boot's internal transition timeline. */
  wipePhase: WipePhase;
  /** Re-show the boot overlay; resets progress to 0. */
  show: (opts?: { mode?: BootPreset; status?: string }) => void;
  /** Host-driven progress (0..1). Cancels the internal auto-driver on first call. */
  setProgress: (value: number) => void;
  /** Override the status label. Pass `null` to revert to auto-derived. */
  setStatus: (label: string | null) => void;
  /** Trigger the finish ramp + wipe. The overlay unmounts itself when done. */
  finish: () => void;
}

const BootCtx = createContext<BootApi | null>(null);

interface BootProviderProps {
  /** Initial boot mode for the first paint. Defaults to "general". */
  initialMode?: BootPreset;
  /** Initial visibility - set false if you want the boot off by default. */
  initiallyVisible?: boolean;
  children: ReactNode;
}

export function BootProvider({
  initialMode = "general",
  initiallyVisible = true,
  children,
}: BootProviderProps) {
  const [visible, setVisible] = useState(initiallyVisible);
  const [mode, setMode] = useState<BootPreset>(initialMode);
  /**
   * `undefined` = auto-driver in charge; number = host-driven.
   * Tracked separately so a `show()` call resets to undefined cleanly.
   */
  const [progress, setProgressState] = useState<number | undefined>(undefined);
  const [status, setStatusState] = useState<string | undefined>(undefined);
  const [finishToken, setFinishToken] = useState(0);

  /** Bumped by `show()` so React unmounts + remounts BootScreen - guarantees
   *  the auto-driver restarts cleanly without smuggling state between runs. */
  const [mountKey, setMountKey] = useState(0);

  const finishLatchRef = useRef(false);
  const [wipePhase, setWipePhase] = useState<WipePhase>("idle");

  const show: BootApi["show"] = (opts) => {
    finishLatchRef.current = false;
    setFinishToken(0);
    setProgressState(undefined);
    setStatusState(undefined);
    setWipePhase("idle");
    if (opts?.mode) setMode(opts.mode);
    if (opts?.status !== undefined) setStatusState(opts.status);
    setMountKey((k) => k + 1);
    setVisible(true);
  };

  const setProgress: BootApi["setProgress"] = (value) => {
    setProgressState(Math.max(0, Math.min(1, Number(value) || 0)));
  };

  const setStatus: BootApi["setStatus"] = (label) => {
    setStatusState(label === null ? undefined : label);
  };

  const finish: BootApi["finish"] = () => {
    if (finishLatchRef.current) return;
    finishLatchRef.current = true;
    setFinishToken((t) => t + 1);
  };

  const handleFinish = () => {
    setVisible(false);
  };

  const value: BootApi = { visible, wipePhase, show, setProgress, setStatus, finish };

  return (
    <BootCtx.Provider value={value}>
      {children}
      {visible && (
        <BootScreen
          key={mountKey}
          mode={mode}
          progress={progress}
          status={status}
          finishNow={finishToken > 0}
          onFinish={handleFinish}
          onPhaseChange={setWipePhase}
        />
      )}
    </BootCtx.Provider>
  );
}

/**
 * Read-only API to drive the boot from anywhere in the tree. Throws if the
 * provider isn't mounted - that's a bug in the host; failing loud is the
 * right call.
 */
export function useBoot(): BootApi {
  const ctx = use(BootCtx);
  if (!ctx) {
    throw new Error("useBoot must be called inside <BootProvider>");
  }
  return ctx;
}

/**
 * Non-throwing boot visibility read. Outside a <BootProvider> (page-level
 * tests, isolated surfaces) there is no splash covering anything, so the
 * answer is simply "not visible".
 */
export function useBootVisible(): boolean {
  return use(BootCtx)?.visible ?? false;
}
